import type { CSSProperties } from 'react';
import { PHOTOS, photoSrc, photoSrcSet, type PhotoName } from '@/features/site/media';
import { cn } from '@/lib/utils';

interface PhotoProps {
  name: PhotoName;
  /** `sizes` attribute describing the rendered width, e.g. "(min-width: 1024px) 50vw, 100vw". */
  sizes: string;
  className?: string;
  /** Above-the-fold images load eagerly with high fetch priority; everything else is lazy. */
  priority?: boolean;
  /** CSS object-position for art-directed crops, e.g. "30% 50%". */
  position?: string;
  /** Override the registry alt text (use "" for purely decorative images). */
  alt?: string;
  zoom?: boolean;
}

/** Responsive, CLS-safe photograph. The parent controls the crop (aspect ratio / height). */
export function Photo({ name, sizes, className, priority, position, alt, zoom }: PhotoProps) {
  const p = PHOTOS[name];
  const focal = position ?? p.position;
  const style: CSSProperties | undefined = focal ? { objectPosition: focal } : undefined;
  return (
    <img
      src={photoSrc(p, p.widths[Math.min(1, p.widths.length - 1)])}
      srcSet={photoSrcSet(p)}
      sizes={sizes}
      width={p.width}
      height={p.height}
      alt={alt ?? p.alt}
      loading={priority ? 'eager' : 'lazy'}
      decoding={priority ? 'sync' : 'async'}
      fetchPriority={priority ? 'high' : undefined}
      style={style}
      className={cn('h-full w-full object-cover', zoom && 'zoom-on-hover', className)}
    />
  );
}
